import { Link } from 'react-router-dom'
import PublicLayout from '../../components/layouts/PublicLayout'
import {
  Brain,
  CheckCircle,
  XCircle,
  Eye,
  FileText,
  Shield,
  AlertTriangle,
} from 'lucide-react'

const permittedActions = [
  {
    action: 'Summarize Session Note',
    description: 'AI can summarize data the user has already entered into session documentation.',
  },
  {
    action: 'Grammar & Clarity Check',
    description: 'AI can suggest grammar and clarity improvements without changing clinical meaning.',
  },
  {
    action: 'Draft Addendum',
    description: 'AI can help draft addendums based on user-provided information.',
  },
  {
    action: 'Behavior Definition Lookup',
    description: 'AI can provide reference information about behavioral concepts.',
  },
  {
    action: 'Insurance Code Lookup',
    description: 'AI can provide factual information about CPT/HCPCS codes.',
  },
]

const forbiddenActions = [
  {
    action: 'Generate Clinical Content',
    description: 'AI cannot create clinical observations, data, or assessments from scratch.',
  },
  {
    action: 'Fabricate Data',
    description: 'AI cannot generate trial data, behavior counts, or any measurable outcomes.',
  },
  {
    action: 'Create Notes from Scratch',
    description: 'AI cannot write session notes without user-entered data as the source.',
  },
  {
    action: 'Modify Locked Content',
    description: 'AI cannot alter any content that has been signed and locked.',
  },
]

const auditTrailFields = [
  'User ID and email',
  'Timestamp of request',
  'AI model and version',
  'Full prompt input (sanitized)',
  'Full AI output',
  'User accept/reject decision',
  'Modifications made by user',
  'Final content used',
  'Human attestation status',
]

export default function AIGovernancePage() {
  return (
    <PublicLayout>
      <div className="bg-gradient-to-b from-primary-50 to-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              AI Governance
            </h1>
            <p className="mt-6 text-lg text-gray-600">
              AI that assists without fabricating. Every action logged.
              Human attestation required. Designed to survive auditor scrutiny.
            </p>
          </div>
        </div>
      </div>

      {/* Safe vs Forbidden Banking */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Permitted Actions */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Permitted AI Actions</h2>
              </div>
              <p className="text-gray-600 mb-6">
                AI is allowed to assist with these activities. All actions are logged.
              </p>
              <div className="space-y-4">
                {permittedActions.map((item) => (
                  <div
                    key={item.action}
                    className="bg-green-50 border border-green-100 rounded-lg p-4"
                  >
                    <h3 className="font-medium text-green-900">{item.action}</h3>
                    <p className="mt-1 text-sm text-green-700">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Forbidden Actions */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Forbidden AI Actions</h2>
              </div>
              <p className="text-gray-600 mb-6">
                AI is blocked from these actions. Attempts are logged and reported.
              </p>
              <div className="space-y-4">
                {forbiddenActions.map((item) => (
                  <div
                    key={item.action}
                    className="bg-red-50 border border-red-100 rounded-lg p-4"
                  >
                    <h3 className="font-medium text-red-900">{item.action}</h3>
                    <p className="mt-1 text-sm text-red-700">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Audit Trail */}
      <div className="bg-gray-900 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-white">
                Complete AI Audit Trail
              </h2>
              <p className="mt-4 text-gray-300">
                Every AI interaction is logged immutably. When an auditor asks
                "was this written by AI?", you have a complete, verifiable record.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {auditTrailFields.map((field) => (
                  <div key={field} className="flex items-center gap-2 text-gray-200">
                    <Eye className="h-4 w-4 text-primary-400" />
                    <span className="text-sm">{field}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <Brain className="h-6 w-6 text-primary-400" />
                <span className="font-semibold text-white">AI Audit Entry #4521</span>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Action</span>
                  <span className="text-green-400">summarize_session_note</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Model</span>
                  <span className="text-white">gpt-4-turbo</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">User Decision</span>
                  <span className="text-white">Accepted with modifications</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Human Attested</span>
                  <span className="text-green-400">Yes</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Unique Content</span>
                  <span className="text-white">78%</span>
                </div>
                <div className="pt-3 border-t border-gray-700">
                  <span className="text-gray-400">Timestamp</span>
                  <p className="text-white">2024-01-15 14:32:00 UTC</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Human Attestation */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Human Attestation Required
            </h2>
            <p className="mt-4 text-gray-600">
              AI-assisted content requires explicit human attestation before it can be
              used in clinical documentation. The user confirms accuracy and takes
              responsibility for the final content.
            </p>

            <div className="mt-12 bg-primary-50 border border-primary-100 rounded-lg p-6 text-left">
              <div className="flex items-start gap-4">
                <Shield className="h-8 w-8 text-primary-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-primary-900">Attestation Statement</h3>
                  <p className="mt-2 text-sm text-primary-800">
                    "I attest that I have reviewed the AI-assisted content, verified its
                    accuracy against actual clinical observations, and confirm that this
                    documentation accurately reflects the services provided and client response."
                  </p>
                  <div className="mt-4 flex items-center gap-4">
                    <button className="btn-primary text-sm">
                      I Attest to Accuracy
                    </button>
                    <button className="btn-secondary text-sm">
                      Reject AI Content
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Similarity Detection */}
      <div className="bg-gray-50 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
              <h2 className="text-2xl font-bold text-gray-900">Similarity Detection</h2>
            </div>
            <p className="text-gray-600 mb-8">
              The system monitors content for excessive similarity to existing documentation.
              High similarity triggers review flags to prevent copy-paste or templated
              documentation that lacks individualization.
            </p>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h3 className="font-medium text-gray-900 mb-4">Minimum Unique Content Requirement</h3>
              <div className="flex items-center gap-4">
                <div className="flex-1 bg-gray-100 rounded-full h-4">
                  <div className="bg-green-500 h-4 rounded-full" style={{ width: '70%' }}></div>
                </div>
                <span className="font-medium text-gray-900">70%</span>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Content with less than 70% unique content compared to the user's recent
                documentation is flagged for review before it can be saved.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-primary-600 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white">
            AI That Survives Audit Scrutiny
          </h2>
          <p className="mt-4 text-lg text-primary-100">
            See how our AI governance protects your documentation integrity.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn bg-white text-primary-600 hover:bg-primary-50 text-base px-6 py-3">
              Request Demo
            </Link>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}
